// Mirror of backend routers/home.py DEFAULT_HOME. Used as the instant fallback so the
// landing page renders rich content on first paint (no soft-404) and never breaks if the
// API is slow/unavailable. The backend is the source of truth once /home-config resolves.
import {
  PHOTO_COACH_PORTRAIT,
  PHOTO_RING_PAD_WORK,
  PHOTO_OUTDOOR_SPLIT,
  PHOTO_OUTDOOR_DRIVEWAY,
  BTB_GRUNGE_TEXT,
  BTB_TSHIRT_RED,
} from "@/lib/brand";

export const DEFAULT_HOME = {
  seo: {
    title: "Online Boxing Lessons via Zoom — Better Through Boxing",
    description:
      "Private online boxing lessons via Zoom with Coach Chris — live 1-on-1 from anywhere in the world, or in-person training in New Jersey. CoachUp's #1 ranked NJ trainer.",
  },
  hero: {
    enabled: true,
    kicker: "Better Through Boxing",
    title_pre: "Online boxing lessons,\nlive with ",
    title_accent: "Coach Chris",
    title_post: ".",
    subtitle:
      "Private online boxing lessons via Zoom, from anywhere in the world. Or work the mitts in person with Coach Chris in New Jersey. CoachUp's #1 ranked NJ trainer. Choose how you want to train.",
    online: { label: "Live Online Training", sublabel: "1-on-1 via Zoom · anywhere in the world", href: "/services" },
    inperson: { label: "In-Person Training", sublabel: "1-on-1 in New Jersey · limited slots", href: "#in-person" },
    badges: [
      { icon: "Trophy", text: "#1 Ranked NJ Trainer" },
      { icon: "Clock", text: "7+ Years Coaching" },
      { icon: "ShieldCheck", text: "Background Check Verified" },
    ],
    bg_image: PHOTO_RING_PAD_WORK,
  },
  services: {
    enabled: true,
    kicker: "Programs",
    heading_line1: "Two ways",
    heading_line2: "to train.",
    intro:
      "The 6-week Basics Program is the flagship. Single drop-in sessions are available for everyone — graduates unlock the $70 alumni rate.",
    featured: {
      tag: "The Method · 6 Weeks",
      title: "BTB Basics Program",
      duration: "6 weeks · 6 sessions",
      location: "Worldwide",
      price: 699,
      price_note: "Full program · one payment",
      desc: "The complete foundation. Six weeks to develop real understanding and proficiency in stance, footwork, defensive blocks, and the Core 4 punches — jab, cross, lead hook, and rear hook. Built for athletes starting from zero or rebuilding their fundamentals correctly.",
      perks: [
        { text: "6 private 40-minute live Zoom sessions (1 per week)", highlight: false },
        { text: "Stance, footwork & defensive blocks proficiency", highlight: false },
        { text: "Core 4 punches: jab · cross · lead hook · rear hook", highlight: false },
        { text: "WhatsApp access to Coach Chris between sessions", highlight: false },
        { text: "Session-by-session notes & benchmarks", highlight: false },
        { text: "Unlock $70 alumni rate on all future sessions", highlight: true },
      ],
      cta_label: "Enroll now",
      cta_href: "/book?package=btb_basics_program",
    },
    single: {
      tag: "Single Session",
      title: "Live Virtual Zoom Session",
      duration: "40 min",
      price: 100,
      alumni_price: 70,
      desc: "Drop-in 40-minute private Zoom session. Sharpen what you know, get expert eyes on your technique. Open to everyone — alumni of the Basics Program unlock the discounted $70 rate.",
      cta_href: "/book?package=virtual_40",
    },
  },
  about: {
    enabled: true,
    kicker: "About the Coach",
    heading_pre: "Mechanics-focused.",
    heading_lead: "Built around ",
    heading_accent: "you",
    heading_post: ".",
    body1: "Coach Chris is the founder of The Flow State Method LLC — CoachUp's #1 ranked trainer in New Jersey with over 7 years of coaching experience. His coaching style is technical and mechanics-focused, combined with functional conditioning. Beginner to advanced — every session is tailored to you.",
    body2: "Coaching is delivered live via Zoom worldwide — and in person for New Jersey athletes. No gym membership required. Just you, the coach, and the work.",
    stats: [
      { value: "#1", label: "Ranked NJ Trainer", accent: true },
      { value: "7+", label: "Years Coaching", accent: false },
      { value: "∞", label: "Reach via Zoom", accent: false },
    ],
    specialties: ["Footwork", "Stance", "Slipping", "Pivot", "Hand Positioning", "Combinations", "Blocking", "Balance", "Clinching"],
    location_line: "Based in New Jersey · Coaching athletes worldwide via Zoom",
    image: PHOTO_COACH_PORTRAIT,
  },
  method: {
    enabled: true,
    kicker: "The Method",
    heading: "More than a workout. A discipline.",
    body1: "Boxing isn't just exercise — it's how we get sharper, stronger, calmer, and more focused. The Flow State Method turns every session into a craft. Skill builds confidence. Confidence builds presence. Presence builds the rest of your life.",
    body2: "That's why we say Better Through Boxing. It's not a slogan — it's the outcome.",
    art_image: BTB_GRUNGE_TEXT,
  },
  in_action: {
    enabled: true,
    kicker: "In Action",
    heading_line1: "The work.",
    heading_line2: "Up close.",
    subtitle: "Real sessions, real athletes, real corrections.",
    photos: [
      { image: PHOTO_RING_PAD_WORK, caption: "Mechanics, repped.", kicker: "Live pad work" },
      { image: PHOTO_OUTDOOR_DRIVEWAY, caption: "Outdoor session", kicker: "" },
      { image: PHOTO_OUTDOOR_SPLIT, caption: "Defensive drills", kicker: "" },
    ],
    instagram_handle: "flow_state_method",
    cta_label: "Book your session",
    cta_href: "/book?package=virtual_40",
  },
  testimonials: {
    enabled: true,
    kicker: "What clients say",
    heading_line1: "Real athletes.",
    heading_line2: "Real results.",
    featured: {
      quote: "Got on the call and got straight to work. Great coach even over Zoom — he made it feel like a good environment, and I don't do well with new people or new stuff.",
      author: "Virtual Client",
      badge: "Verified Zoom session",
    },
    items: [
      { quote: "Chris is great. I feel like I'm getting a good workout while also getting technique and strategy down. Easy going so you don't feel pressure or intimidated — perfect for a beginner.", author: "Beginner Athlete" },
      { quote: "Pleased with his knowledge in the sport. He doesn't just show you how to do something — he explains the movement in such detail that you can feel it.", author: "Returning Client" },
      { quote: "Great coach, knows his stuff.", author: "Verified Client" },
    ],
  },
  apparel: {
    enabled: true,
    kicker: "Apparel",
    badge: "Now Live",
    heading: "Wear the method.",
    body: "Better Through Boxing apparel — designed in-house, built for athletes who live the discipline. Tees, fleece, accessories — all available now.",
    cta_label: "Shop now",
    cta_href: "https://the-flows-state-method-llc-shop.fourthwall.com/",
    image: BTB_TSHIRT_RED,
  },
  in_person: {
    enabled: true,
    kicker: "In-Person",
    badge: "NJ Only",
    heading_line1: "Live in",
    heading_line2: "New Jersey?",
    heading_accent: "Train in person.",
    body: "Want to work the mitts in person? Coach Chris offers limited 1-on-1 in-person sessions in select North/Central Jersey locations. Tell us where you're at and what you're after — if we're a fit, you'll hear back within 48 hours.",
    bullets: [
      "Limited availability — coach-vetted only",
      "Mitts, footwork, sparring prep",
      "Beginner to competitor",
    ],
  },
  closer: {
    enabled: true,
    kicker: "Train With Coach Chris",
    heading_line1: "Step in.",
    heading_line2: "Get to work.",
    body: "Whether you're just starting out or rebuilding your fundamentals, the work happens together — live on Zoom or in person in NJ. Lock in your first session today.",
    primary_label: "Start the Basics Program",
    primary_href: "/book?package=btb_basics_program",
    secondary_label: "Book a Single Session",
    secondary_href: "/book?package=virtual_40",
    image: PHOTO_COACH_PORTRAIT,
  },
  contact: {
    enabled: true,
    kicker: "Message the coach",
    heading_line1: "Have a question?",
    heading_line2: "Reach out.",
    body: "Not ready to book? Send a message — Coach Chris will get back personally within 48 hours.",
    instagram_handle: "flow_state_method",
  },
  instagram_feed: { enabled: true, title: "Latest from the gym." },
  get_the_app: { enabled: true },
  google: {
    enabled: true,
    kicker: "Find Us On Google",
    heading: "The Flow State Method on Google",
    subtext: "Coaching athletes across New Jersey and worldwide via Zoom. Find us on Google, see what clients are saying — and leave your own review to help others find the gym.",
    maps_url: "https://maps.google.com/maps?cid=12009279507527467511",
    review_url: "https://search.google.com/local/writereview?placeid=ChIJQ5vlETyWYWcR94GNjhWIqaY",
    map_embed_url: "https://maps.google.com/maps?cid=12009279507527467511&output=embed",
    address: "Somerset County and Central New Jersey",
    phone: "(732) 648-4949",
  },
};
